"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { ExternalLink, Info, Maximize2, Minimize2, Sun } from "lucide-react";
import { useRef, useState } from "react";

interface PropertySunlightProps {
  latitude: number | null;
  longitude: number | null;
  address: string;
  className?: string;
}

export function PropertySunlight({
  latitude,
  longitude,
  address,
  className,
}: PropertySunlightProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle iframe load event
  const handleIframeLoad = () => {
    setIsLoading(false);
    setIframeLoaded(true);
  };

  // If no coordinates, show placeholder
  if (!latitude || !longitude) {
    return (
      <Card className={cn("p-6 bg-white", className)}>
        <div className="flex items-center gap-2 text-muted-foreground mb-4">
          <Sun className="h-5 w-5" />
          <h3 className="text-lg font-semibold text-gray-900">
            Sunlight Analysis
          </h3>
        </div>
        <div className="flex items-center justify-center h-40 bg-gray-50 rounded-lg">
          <div className="text-center text-gray-500">
            <p>Sunlight data not available for this property</p>
          </div>
        </div>
      </Card>
    );
  }

  // Construct Shadowmap URL with parameters
  const shadowmapUrl = `https://masterkey.poweredby.shadowmap.org/?lat=${latitude}&lng=${longitude}&zoom=19.00&floor=3.0&hud=true&vq=2`;

  return (
    <Card
      className={cn(
        "bg-white overflow-hidden transition-all duration-300",
        isExpanded ? "fixed inset-4 z-50 p-0 flex flex-col" : "p-6",
        className,
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "flex items-center justify-between shrink-0",
          isExpanded ? "p-4 border-b" : "mb-4",
        )}
      >
        <div className="flex items-center gap-2">
          <Sun className="h-5 w-5 text-amber-500" />
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">
                Sunlight Analysis
              </h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6">
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p>
                      Analyze how sunlight affects this property throughout the
                      day and year. Powered by Shadowmap.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-xs text-muted-foreground">
              Data provided by{" "}
              <a
                href="https://shadowmap.org/?utm_source=masterkey&utm_medium=referral&utm_campaign=property_analysis"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black hover:text-gray-700 transition-colors font-semibold"
              >
                Shadowmap
              </a>
            </p>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1.5"
        >
          {isExpanded ? (
            <>
              <Minimize2 className="h-4 w-4" />
              <span className="hidden sm:inline">Minimize</span>
            </>
          ) : (
            <>
              <Maximize2 className="h-4 w-4" />
              <span className="hidden sm:inline">Fullscreen</span>
            </>
          )}
        </Button>
      </div>

      {/* Content */}
      <div className={cn("relative flex-col", isExpanded ? "h-full" : "")}>
        <div
          ref={containerRef}
          className={cn("relative", isExpanded ? "h-full" : "h-[400px]")}
        >
          {/* Loading state */}
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-amber-500 rounded-full animate-spin mb-4" />
              <p className="text-gray-600">Loading sunlight data...</p>
            </div>
          )}

          {/* Shadowmap iframe */}
          <div className="absolute inset-0">
            <motion.iframe
              src={shadowmapUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
              allow="clipboard-write; fullscreen"
              loading="lazy"
              onLoad={handleIframeLoad}
              className={cn(
                "transition-opacity duration-500 w-full h-full",
                iframeLoaded ? "opacity-100" : "opacity-0",
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: iframeLoaded ? 1 : 0 }}
              title={`Sunlight analysis for ${address}`}
            />
          </div>
        </div>

        {/* CTA Section */}
        {iframeLoaded && !isExpanded && (
          <div className="mt-4 flex items-center justify-center px-4">
            <a
              href={`https://app.shadowmap.org/?lat=${latitude}&lng=${longitude}&zoom=19.00&floor=3.0&hud=true&vq=2&discount=masterkey10&utm_source=masterkey&utm_medium=referral&utm_campaign=property_analysis`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full max-w-md group"
            >
              <div className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-50/80 hover:bg-gray-50 border border-gray-100 rounded-lg transition-all duration-200 hover:shadow-sm">
                <div className="flex items-center gap-1.5">
                  <Sun className="h-4 w-4 text-amber-500" />
                  <span className="text-sm text-gray-600">
                    Unlock 3D sunlight insights on{" "}
                    <span className="text-black font-semibold">Shadowmap</span>
                  </span>
                </div>
                <ExternalLink className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </div>
            </a>
          </div>
        )}
      </div>
    </Card>
  );
}
